import { useEffect } from 'react';

/**
 * Google Tag Manager Script Component
 * 
 * This component initializes Google Tag Manager and injects the GTM snippet
 * into the page. It should be added once at the root level of the app.
 * 
 * Environment Variables Required:
 * - VITE_GTM_ID: Your Google Tag Manager Container ID (e.g., GTM-XXXXXXX)
 */
export function GTMScript() {
  useEffect(() => {
    const GTM_ID = import.meta.env.VITE_GTM_ID;

    // Only load GTM in production and if GTM_ID is configured
    if (!GTM_ID || import.meta.env.DEV) {
      console.log('[Analytics] GTM disabled in development');
      return;
    }

    // Prevent double-loading
    if (window.dataLayer) {
      console.log('[Analytics] GTM already loaded');
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    // Inject GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);

    // Inject noscript iframe fallback
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    console.log('[Analytics] GTM initialized:', GTM_ID);
  }, []);

  return null;
}

// TypeScript declarations for window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}
